import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from '../config/api';

/**
 * Session Manager - Manages local storage and session persistence
 * Handles secure token storage, validation, and cleanup
 */
class SessionManager {
  /**
   * Store authentication token securely in localStorage
   */
  static setToken(token, storageKey = TOKEN_STORAGE_KEY) {
    if (!token) {
      console.warn('[SessionManager] Attempted to store empty token');
      return false;
    }

    try {
      // Store token with timestamp for expiration checking
      const tokenData = {
        token,
        timestamp: Date.now(),
        expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours default
      };
      
      localStorage.setItem(storageKey, JSON.stringify(tokenData));
      return true;
    } catch (error) {
      console.error('Failed to store token:', error);
      return false;
    }
  }

  /**
   * Retrieve authentication token from localStorage
   */
  static getToken(storageKey = TOKEN_STORAGE_KEY) {
    try {
      const tokenDataStr = localStorage.getItem(storageKey);
      if (!tokenDataStr) {
        return null;
      }

      const tokenData = JSON.parse(tokenDataStr);
      
      // Check if token is expired
      if (tokenData.expiresAt && Date.now() > tokenData.expiresAt) {
        console.log('[SessionManager] Token expired, removing...');
        this.removeToken(storageKey);
        return null;
      }
      return tokenData.token;
    } catch (error) {
      console.error('Failed to retrieve token:', error);
      this.removeToken(storageKey); // Clean up corrupted data
      return null;
    }
  }

  /**
   * Remove authentication token from localStorage
   */
  static removeToken(storageKey = TOKEN_STORAGE_KEY) {
    try {
      localStorage.removeItem(storageKey);
      return true;
    } catch (error) {
      console.error('Failed to remove token:', error);
      return false;
    }
  }

  /**
   * Check if token exists and is valid
   */
  static isTokenValid(storageKey = TOKEN_STORAGE_KEY) {
    const token = this.getToken(storageKey);
    if (!token || token.length === 0) {
      return false;
    }
    
    // Basic token format validation (should be a reasonable length)
    if (token.length < 10) {
      console.warn('Token appears to be invalid (too short), clearing...');
      this.removeToken(storageKey);
      return false;
    }
    
    return true;
  }

  /**
   * Store user information
   */
  static setUser(userInfo, storageKey = USER_STORAGE_KEY) {
    if (!userInfo) {
      console.warn('Attempted to store empty user info');
      return false;
    }

    try {
      localStorage.setItem(storageKey, JSON.stringify(userInfo));
      return true;
    } catch (error) {
      console.error('Failed to store user info:', error);
      return false;
    }
  }

  /**
   * Retrieve user information
   */
  static getUser(storageKey = USER_STORAGE_KEY) {
    try {
      const userStr = localStorage.getItem(storageKey);
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Failed to retrieve user info:', error);
      this.removeUser(storageKey); // Clean up corrupted data
      return null;
    }
  }

  /**
   * Remove user information
   */
  static removeUser(storageKey = USER_STORAGE_KEY) {
    try {
      localStorage.removeItem(storageKey);
      return true;
    } catch (error) {
      console.error('Failed to remove user info:', error);
      return false;
    }
  }

  /**
   * Clear all session data (token, user info, cached data)
   */
  static clearSession(tokenKey = TOKEN_STORAGE_KEY, userKey = USER_STORAGE_KEY) {
    try {
      console.log('[SessionManager] Clearing all session data');
      this.removeToken(tokenKey);
      this.removeUser(userKey);
      
      // Clear any other session-related data
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('seemanchal_') || key.includes('auth-storage'))) {
          keysToRemove.push(key);
        }
      }
      
      console.log('[SessionManager] Removing keys:', keysToRemove);
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Also clear any Zustand persist storage
      localStorage.removeItem('auth-storage');
      localStorage.removeItem('client-auth-storage');
      
      return true;
    } catch (error) {
      console.error('Failed to clear session:', error);
      return false;
    }
  }

  /**
   * Check if user is authenticated (has valid token)
   */
  static isAuthenticated(storageKey = TOKEN_STORAGE_KEY) {
    return this.isTokenValid(storageKey);
  }

  /**
   * Update token expiration time
   */
  static updateTokenExpiration(expiresIn = 24 * 60 * 60 * 1000, storageKey = TOKEN_STORAGE_KEY) {
    try {
      const tokenDataStr = localStorage.getItem(storageKey);
      if (!tokenDataStr) {
        return false;
      }

      const tokenData = JSON.parse(tokenDataStr);
      tokenData.expiresAt = Date.now() + expiresIn;
      
      localStorage.setItem(storageKey, JSON.stringify(tokenData));
      return true;
    } catch (error) {
      console.error('Failed to update token expiration:', error);
      return false;
    }
  }

  /**
   * Get token expiration time
   */
  static getTokenExpiration(storageKey = TOKEN_STORAGE_KEY) {
    try {
      const tokenDataStr = localStorage.getItem(storageKey);
      if (!tokenDataStr) {
        return null;
      }

      const tokenData = JSON.parse(tokenDataStr);
      return tokenData.expiresAt ? new Date(tokenData.expiresAt) : null;
    } catch (error) {
      console.error('Failed to get token expiration:', error);
      return null;
    }
  }

  /**
   * Check if token will expire soon (within specified minutes)
   */
  static isTokenExpiringSoon(minutesThreshold = 5, storageKey = TOKEN_STORAGE_KEY) {
    const expiration = this.getTokenExpiration(storageKey);
    if (!expiration) {
      return false;
    }

    const thresholdTime = Date.now() + (minutesThreshold * 60 * 1000);
    return expiration.getTime() < thresholdTime;
  }

  /**
   * Store cached data with expiration
   */
  static setCachedData(key, data, expirationMinutes = 30) {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        expiresAt: Date.now() + (expirationMinutes * 60 * 1000)
      };
      
      localStorage.setItem(`seemanchal_cache_${key}`, JSON.stringify(cacheData));
      return true;
    } catch (error) {
      console.error('Failed to cache data:', error);
      return false;
    }
  }

  /**
   * Retrieve cached data if not expired
   */
  static getCachedData(key) {
    try {
      const cacheDataStr = localStorage.getItem(`seemanchal_cache_${key}`);
      if (!cacheDataStr) {
        return null;
      }

      const cacheData = JSON.parse(cacheDataStr);
      
      // Check if cache is expired
      if (cacheData.expiresAt && Date.now() > cacheData.expiresAt) {
        localStorage.removeItem(`seemanchal_cache_${key}`);
        return null;
      }

      return cacheData.data;
    } catch (error) {
      console.error('Failed to retrieve cached data:', error);
      localStorage.removeItem(`seemanchal_cache_${key}`);
      return null;
    }
  }

  /**
   * Clear all cached data
   */
  static clearCache() {
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('seemanchal_cache_')) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      return true;
    } catch (error) {
      console.error('Failed to clear cache:', error);
      return false;
    }
  }
}

export default SessionManager;